import React, { useEffect, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '@mui/material';
import { GripVertical, MessageCircle, SquarePen, Trash2 } from 'lucide-react';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import { composeRefs } from 'src/helpers/compose-refs';
import { Flag } from 'src/helpers/feature-flags';

import { PlaybookTextarea } from './playbook-textarea';
import { useRuleDetailsStyles } from './rule-details-styles';
import type { PlaybookDetailRule } from './types';
import type { PositionCommitProps } from './use-rule-positions';

type Position = PlaybookDetailRule['positions'][number];

interface PositionCardProps {
  /** False for a rule's only position, which it cannot be without. */
  canDelete: boolean;
  index: number;
  onCommit: (props: PositionCommitProps) => void;
  onDelete: () => void;
  position: Position;
  /** Focus the position text on mount, for a position that was just added. */
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * One stance of a rule: its requirement text, the guidance the AI comments
 * with, and the grip that drags it. Index 0 is the Starting Position; any
 * position can be deleted as long as the rule keeps one.
 */
export const PositionCard = React.memo(function PositionCard({
  autoFocus,
  canDelete,
  disabled = false,
  index,
  onCommit,
  onDelete,
  position,
}: PositionCardProps): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: position.id,
  });
  const isStartingPosition = index === 0;
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const positionTextRef = useRef<HTMLTextAreaElement | null>(null);
  const positionLabel = isStartingPosition ? 'Starting Position' : `Fallback Position ${index}`;
  // A blank position has nothing to lose, so it goes without asking.
  const isBlank = !position.ruleText.trim() && !(position.aiGuidance ?? '').trim();

  function handleDeleteClick(): void {
    if (isBlank) onDelete();
    else setDeleteConfirmOpen(true);
  }

  function handleEditClick(): void {
    const input = positionTextRef.current;
    if (!input) return;
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }

  return (
    <div
      ref={setNodeRef}
      className={classes.positionRow}
      data-cy={Select.playbooks.positionCard}
      style={{
        opacity: isDragging ? 0.4 : 1,
        /**
         * Translate only. `CSS.Transform` would also apply dnd-kit's scale,
         * which is the ratio between the card's height before and after the
         * reorder — position cards differ in height by several lines, so the
         * card would visibly stretch or squash on drop.
         */
        transform: CSS.Translate.toString(transform),
        transition,
      }}
    >
      <span className={classes.dragHandle} {...listeners} {...attributes}>
        <GripVertical className={classes.dragHandleIcon} size={16} />
      </span>
      <div className={classes.card}>
        <div className={classes.cardActions}>
          <AlineTooltip arrow placement="top" title="Edit position">
            <IconButton
              data-cy={Select.playbooks.positionEdit}
              disabled={disabled}
              size="small"
              onClick={handleEditClick}
            >
              <SquarePen size={16} />
            </IconButton>
          </AlineTooltip>
          <AlineTooltip
            arrow
            placement="top"
            title={canDelete ? 'Delete position' : 'A rule needs at least one position'}
          >
            {/* Wrapped so the tooltip still works while the button is disabled. */}
            <span>
              <IconButton
                data-cy={Select.playbooks.positionDelete}
                disabled={disabled || !canDelete}
                size="small"
                onClick={handleDeleteClick}
              >
                <Trash2 size={16} />
              </IconButton>
            </span>
          </AlineTooltip>
        </div>
        <PositionField
          autoFocus={autoFocus}
          className={classes.field}
          dataCy={Select.playbooks.positionText}
          disabled={disabled}
          label={positionLabel}
          placeholder="Type your playbook position..."
          textareaRef={positionTextRef}
          value={position.ruleText}
          onCommit={ruleText => onCommit({ ruleText })}
        />
        {Flag.PLAYBOOK_COMMENT_GUIDANCE && (
          <PositionField
            className={classes.guidanceField}
            dataCy={Select.playbooks.positionGuidance}
            disabled={disabled}
            label={
              <>
                <MessageCircle size={16} />
                <span>Comment Guidance</span>
              </>
            }
            placeholder="Type guidance for AI to generate a comment..."
            value={position.aiGuidance ?? ''}
            // An empty box means no guidance, which is null rather than ''.
            onCommit={value => onCommit({ aiGuidance: value.trim() ? value : null })}
          />
        )}
      </div>
      <ConfirmationDialog
        message={`Are you sure you want to delete the ${positionLabel}? This action cannot be undone.`}
        open={deleteConfirmOpen}
        primaryDataCy={Select.playbooks.positionDeleteConfirm}
        title="Delete position?"
        onClickPrimary={() => {
          setDeleteConfirmOpen(false);
          onDelete();
        }}
        onClose={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
});

interface PositionFieldProps {
  className: string;
  dataCy: string;
  label: React.ReactNode;
  onCommit: (value: string) => void;
  placeholder: string;
  value: string;
  autoFocus?: boolean;
  disabled?: boolean;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}

/** A labelled field of a position card. */
function PositionField({
  autoFocus,
  className,
  dataCy,
  disabled,
  label,
  onCommit,
  placeholder,
  textareaRef,
  value,
}: PositionFieldProps): JSX.Element {
  const { classes } = useRuleDetailsStyles();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  /**
   * Focus once, when asked. An effect rather than the input's own autoFocus:
   * the card mounts off the cache write before the add mutation resolves and
   * sets the id that asks for focus, so at mount the answer is still "no".
   * The guard keeps a re-run with the same input (a tab switch) a no-op.
   */
  const hasFocusedRef = useRef(false);
  useEffect(() => {
    if (!autoFocus || hasFocusedRef.current) return;
    hasFocusedRef.current = true;
    inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <div className={classes.fieldGroup}>
      <div className={classes.fieldLabel}>{label}</div>
      <PlaybookTextarea
        className={className}
        dataCy={dataCy}
        disabled={disabled}
        inputRef={composeRefs(inputRef, textareaRef)}
        placeholder={placeholder}
        value={value}
        onPersist={onCommit}
      />
    </div>
  );
}
